import { z } from 'zod';
import { isoDateTimeSchema, localeSchema } from '../common/primitives.js';
import { customerMatchSchema, partyInputSchema } from '../transfers/parties.js';
/**
 * Customer capture through the shared tablet service (transfers.md section 5,
 * tablet.md section 2, owner decision 5 of 4 October 2026).
 *
 * Transfers no longer pairs tablets or serves a tablet page. The employee
 * sends a draft transfer to a tablet paired in the tablet service, or to
 * هذا الجهاز: a one-time link of the tablet service's form page that the
 * employee's browser opens full screen and that returns to the draft. Either
 * way a **capture task** links the draft to the shared task; the API polls the
 * shared task and, once submitted, keeps its own copy of the data and the
 * signature.
 */
// ---------------------------------------------------------------------------
// Tablets of the session branch (the picker)
// ---------------------------------------------------------------------------
/** One tablet of the session branch, as the tablet service reports it. */
export const tabletOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Holds its live connection to the tablet service right now. */
    online: z.boolean(),
    /** Shows a task of any connected system right now. */
    busy: z.boolean(),
    lastSeenAt: isoDateTimeSchema.nullable(),
});
/**
 * GET /tablets/options. `configured` is false while this server has no tablet
 * service link; the employee then cannot send the form anywhere.
 * `available` is false when the tablet service could not be reached.
 */
export const tabletOptionsSchema = z.object({
    configured: z.boolean(),
    available: z.boolean(),
    tablets: z.array(tabletOptionSchema),
});
// ---------------------------------------------------------------------------
// Capture tasks
// ---------------------------------------------------------------------------
/** Where the customer fills the form: a paired tablet or هذا الجهاز. */
export const captureTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('TABLET'), tabletId: z.string().min(1) }),
    z.object({ kind: z.literal('THIS_DEVICE') }),
]);
/**
 * SENT / OPENED: waiting for the customer (OPENED: the form is on screen).
 * SUBMITTED: the customer pressed تم and the data and signature were saved
 * here. COMPLETED: the transfer was posted. CANCELLED: the employee discarded
 * the draft, changed signed data, or sent a newer task. EXPIRED: nobody
 * answered within the tablet service's time limit. FAILED: the submission
 * could not be saved here (PRD-11); the employee sends the form again.
 */
export const CAPTURE_TASK_STATUSES = [
    'SENT',
    'OPENED',
    'SUBMITTED',
    'COMPLETED',
    'CANCELLED',
    'EXPIRED',
    'FAILED',
];
export const captureTaskStatusSchema = z.enum(CAPTURE_TASK_STATUSES);
/** The statuses in which the customer may still answer. */
export const OPEN_CAPTURE_TASK_STATUSES = ['SENT', 'OPENED'];
/** Signatures are PNG files of at most 512 KB. */
export const SIGNATURE_MAX_BYTES = 512 * 1024;
export const SIGNATURE_CONTENT_TYPE = 'image/png';
/** Why a submission could not be saved (status FAILED). */
export const CAPTURE_FAILURES = ['SIGNATURE_INVALID', 'DATA_INVALID', 'NOT_COLLECTED'];
export const captureFailureSchema = z.enum(CAPTURE_FAILURES);
/** Employee view of a capture task: what the customer entered, beside the Customers matches. */
export const captureTaskViewSchema = z.object({
    id: z.string(),
    transferId: z.string(),
    status: captureTaskStatusSchema,
    target: z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('TABLET'),
            tablet: z.object({
                id: z.string(),
                name: z.string(),
                /** Null when the tablet service could not be asked. */
                online: z.boolean().nullable(),
            }),
        }),
        z.object({ kind: z.literal('THIS_DEVICE') }),
    ]),
    failure: captureFailureSchema.nullable(),
    createdAt: isoDateTimeSchema,
    submittedAt: isoDateTimeSchema.nullable(),
    submission: z
        .object({
        language: localeSchema,
        receiver: partyInputSchema,
        sender: partyInputSchema.nullable(),
        signature: z.object({ id: z.string(), signedAt: isoDateTimeSchema }),
        receiverMatch: customerMatchSchema,
        senderMatch: customerMatchSchema.nullable(),
    })
        .nullable(),
});
/**
 * The answer of POST /transfers/drafts/:id/send and
 * POST /capture-tasks/:id/resend. For هذا الجهاز `link` is the tablet
 * service's one-time form address; the browser opens it at once and it
 * returns to the draft. It is never stored or shown again.
 */
export const sentCaptureTaskSchema = captureTaskViewSchema.extend({
    link: z.string().url().nullable(),
});
/** POST /capture-tasks/:id/resend — إعادة الإرسال إلى التابلت, optionally elsewhere. */
export const resendCaptureTaskSchema = z.object({ target: captureTargetSchema.optional() });
/** The employee screen polls the draft every two seconds while the task is open (tablet.md 2.6). */
export const CAPTURE_POLL_INTERVAL_MS = 2000;
//# sourceMappingURL=contract.js.map